import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import payments from "../../api/payments";
import BuyWaterSteps from "../../components/BuyWaterComponents/BuyWaterSteps";
import Loader from "../../components/GeneralComponents/Loader";
import { getServerError } from "../../helper/misc";
import { usePurchases } from "../../helper/purchases";
import { cubicMetres } from "../../constants";
import Logo from "../../components/GeneralComponents/Logo";

function Receipt() {
  const purchases = usePurchases();
  const { tx_ref } = useParams();
  const componentRef = useRef();
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState({});
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: receipt?.receipt_no || "Paywater Purchase Receipt",
    onAfterPrint: () => toast.success("Receipt printed succesfully"),
  });

  const getReceipt = async () => {
    try {
      const { data } = await payments.getReceipt(tx_ref);
      setReceipt(data?.data);
      setLoading(false);
    } catch (e) {
      toast.error(getServerError(e));
      setLoading(false);
    }
  };

  useEffect(() => {
    getReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pt-5 flex flex-col space-y-[47px]">
      {purchases.purchaseDetails?.amount && <BuyWaterSteps />}
      <div className="bg-pw-card lg:rounded-[30px] max-w-[1004.68px] w-full lg:mr-[50px] p-4 lg:p-[50px]">
        {loading ? (
          <div className="h-[300px] flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-center !w-full">
              <button type="button" className="opacity-0 pointer-events-none">
                Print
              </button>
              <h2 className="font-semibold text-pw-blue text-center lg:text-start text-2xl md:text-3xl mx-auto ">
                Purchase Successful
                {receipt?.tenant && <br />}
              </h2>
              <button
                onClick={handlePrint}
                type="button"
                className="font-bold text-[17px] text-pw-blue"
              >
                Print
              </button>
            </div>
            <div
              ref={componentRef}
              className="bg-pw-card pt-[30px] pb-[47px] mt-[31px]"
            >
              {receipt?.tenant && (
                <p className="text-center text-sm text-black my-10">
                  {receipt.tenant}
                </p>
              )}
              <div className="grid grid-cols-2 gap-x-[27px] gap-y-4">
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Date:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium">
                    {moment(receipt?.date).format("MMM DD, YYYY hh:mm a")}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Receipt No:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium whitespace-pre-wrap break-all">
                    {receipt?.receipt_no}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Ref:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium whitespace-pre-wrap break-all">
                    {receipt?.reference}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Customer:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium whitespace-pre-wrap break-all">
                    {receipt?.customer}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Address:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium whitespace-pre-wrap break-all">
                    {receipt?.address}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Meter Number:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium whitespace-pre-wrap break-all">
                    {receipt?.meter_number}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Payment Method:
                  </span>
                </div>
                <div className="mr-auto">
                  <span className="textpw-dark text-[17px] font-medium whitespace-pre-wrap break-all">
                    {receipt?.payment_method}
                  </span>
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Transaction Amount:
                  </span>
                </div>
                <div className="mr-auto">
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    value={Number(receipt?.transaction_amount)}
                    className="textpw-dark text-[17px] font-medium"
                    displayType="text"
                    prefix="₦"
                  />
                </div>
                {/* charges */}
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Transaction Charges:
                  </span>
                </div>
                <div className="mr-auto">
                  <NumericFormat
                    thousandSeparator
                    fixedDecimalScale
                    decimalScale={2}
                    value={Number(receipt?.transaction_charges || 0)}
                    className="textpw-dark text-[17px] font-medium"
                    displayType="text"
                    prefix="₦"
                  />
                </div>
                {/* total */}
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Total Amount Paid:
                  </span>
                </div>
                <div className="mr-auto">
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    value={Number(receipt?.total || 0)}
                    className="textpw-dark text-[17px] font-medium"
                    displayType="text"
                    prefix="₦"
                  />
                </div>
                <div className="ml-auto">
                  <span className="textpwpw-grey2 text-[17px] font-medium">
                    Units:
                  </span>
                </div>
                <div className="mr-auto">
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    value={Number(receipt?.units || 0)}
                    className="textpw-dark text-[17px] font-medium"
                    displayType="text"
                    suffix={` ${cubicMetres}`}
                  />
                </div>
              </div>
              <div className="flex items-center justify-center">
                <Logo />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Receipt;
